import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Table, DatePicker, Space, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import WealthieBreadCrumb from '../../components/BreadCrumb';
import { getLogEntriesApi } from '../../api';
import moment from 'moment';
import { get } from 'lodash';
import { Link } from 'react-router-dom';
import showNotification from '../../components/Notification';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../store/reducers/commonSlice';
import withLoading from '../../helpers/withLoadingHOC';
import { LogEntry } from '../../types';

const { RangePicker } = DatePicker;

const breadCrumbs = [
  {
    name: 'Wealthie',
  },
  {
    name: 'Log',
  },
];

const ExpandedView = ({
  id,
  name,
  action_timestamp,
  action_notes,
  admin_user_id,
  gift_card_id,
  action_description,
  parent_id,
  child_id,
  updated_at,
}: LogEntry): ReactElement => (
  <div>
    <p>ID: {id}</p>
    <p>Name: {name}</p>
    <p>Date: {action_timestamp}</p>
    <p>Note: {action_notes}</p>
    <p>Admin User Id: {admin_user_id}</p>
    <p>
      Gift Card Id:{' '}
      <Link to={`/gift-card/${gift_card_id}`}>{gift_card_id}</Link>
    </p>
    <p>Description: {action_description}</p>
    <p>Updated at: {updated_at}</p>
    <p>
      Parent Id: <Link to={`/users/${parent_id}`}>{parent_id}</Link>
    </p>
    <p>
      Child Id: <Link to={`/child/${parent_id}/${child_id}`}>{child_id}</Link>
    </p>
  </div>
);

function Log(): ReactElement {
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<Array<LogEntry>>([]);
  const [current, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filterDateRange, setFilterDateRange] = useState<any>(null);
  // eslint-disable-next-line
  const handleSearch = useCallback((_selectedKeys, confirm, _dataIndex?) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    // handle reset
    setFilterDateRange(null);
    clearFilters();
  }, []);

  const getColumnDateProps = useCallback(
    (dataIndex) => ({
      filterDropdown: function renderFilter({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) {
        return (
          <div>
            <RangePicker
              value={filterDateRange}
              onChange={(values) => {
                setSelectedKeys(values ? values : []);
                setFilterDateRange(values);
              }}
              showNow
            />
            <Space size={10}>
              <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90, marginLeft: 10 }}
              >
                Search
              </Button>
              <Button
                onClick={() => handleReset(clearFilters)}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      },
    }),
    [handleReset, handleSearch, filterDateRange],
  );

  const collumns: any = useMemo(
    () => [
      {
        title: 'Id',
        dataIndex: 'id',
      },
      {
        title: 'Date',
        dataIndex: 'action_timestamp',
        render: function renderDate(text: string) {
          return <div>{moment(new Date(text)).format('DD/MM/YYYY hh:ss')}</div>;
        },
        sorter: function sortDate(preObjs: LogEntry, nextObj: LogEntry) {
          return (
            moment(preObjs.action_timestamp).unix() -
            moment(nextObj.action_timestamp).unix()
          );
        },
        sortDirections: ['descend'],
        ...getColumnDateProps('action_timestamp'),
      },
      {
        title: 'Action Description',
        dataIndex: 'action_description',
      },
      {
        title: 'Parent Id',
        dataIndex: 'parent_id',
        render: function renderFirstName(text: string, row: LogEntry) {
          return <Link to={`/users/${row.parent_id}`}>{text}</Link>;
        },
      },
      {
        title: 'Child Id',
        dataIndex: 'child_id',
        render: function renderFirstName(text: string, row: LogEntry) {
          return (
            <Link to={`/child/${row.parent_id}/${row.child_id}`}>{text}</Link>
          );
        },
      },
    ],
    [getColumnDateProps],
  );

  useEffect(() => {
    dispatch(setLoading(true));
    const startDate = moment().subtract(1, 'months').format('DD/MM/YYYY');
    const endDate = moment().add(2, 'days').format('DD/MM/YYYY');
    getLogEntriesApi(0, 10, startDate, endDate)
      .then((res) => {
        const totalCounts = get(res, 'pagination.totalCount');
        setTotal(totalCounts);
        setTableData(res.data);
        dispatch(setLoading(false));
      })
      .catch(() => dispatch(setLoading(false)));
  }, [dispatch]);

  const onPaginationChange = useCallback(
    async (pagination: any, filter: any) => {
      let startDate, endDate;
      if (filter && filter.action_timestamp) {
        const filterRange = filter.action_timestamp;
        startDate = filterRange[0].format('DD/MM/YYYY');
        endDate = filterRange[1].format('DD/MM/YYYY');
      } else {
        startDate = moment().subtract(1, 'months').format('DD/MM/YYYY');
        endDate = moment().format('DD/MM/YYYY');
      }
      dispatch(setLoading(true));
      try {
        const res = await getLogEntriesApi(
          pagination.pageSize * (pagination.current - 1),
          pagination.pageSize,
          startDate,
          endDate,
        );
        setPageSize(pagination.pageSize);
        setTableData(res.data);
        setCurrentPage(pagination.current);
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(setLoading(false));
        showNotification({
          type: 'error',
          message: 'Somethings went wrong, please try again',
        });
      }
    },
    [dispatch],
  );

  return (
    <>
      <WealthieBreadCrumb breadcrumbConfig={breadCrumbs} />
      <Table
        expandable={{
          expandedRowRender: function renderExpandedLog(record) {
            return <ExpandedView {...record} />;
          },
        }}
        dataSource={tableData}
        rowKey="id"
        pagination={{
          total,
          pageSize,
          current,
        }}
        columns={collumns}
        onChange={onPaginationChange}
      />
    </>
  );
}

export default withLoading(Log);
